import {
  formatDateTime,
  formatkWhEnergy,
  toPayloadDateBroker,
} from '@energy-stacks/shared';
import CardWidget from '../CardWidget';
import { useGetSessionNumberAndConsumptionSlicesQuery } from '@energy-stacks/broker/feature-dashboard-data';
import { Props } from 'react-apexcharts';
import { IconBolt } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface EnergyWidgetProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export const EnergyWidget = ({ startDate, endDate }: EnergyWidgetProps) => {
  const { t } = useTranslation('dashboard');

  const { data, isLoading } = useGetSessionNumberAndConsumptionSlicesQuery(
    {
      from: startDate ? toPayloadDateBroker(startDate) : undefined,
      to: endDate ? toPayloadDateBroker(endDate) : undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const sessions =
    data?.timeSlices?.map((transaction) => {
      const energy = transaction.energyConsumption;
      const dateSlice = transaction.timesliceStart;
      return {
        energy: Math.round(energy),
        timestamp: formatDateTime(dateSlice),
      };
    }) || [];
  const maxEnergy = Math.max(...sessions.map((session) => session.energy));
  const totalEnergyConsumption = sessions.reduce((sum, s) => sum + s.energy, 0);

  const chartData: Props = {
    type: 'line',
    height: 90,
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        sparkline: {
          enabled: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        padding: {
          top: 7,
          bottom: 7,
          left: 7,
          right: 7,
        },
      },
      colors: ['#fff'],
      fill: {
        type: 'solid',
        opacity: 1,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      yaxis: {
        min: 0,
        max: maxEnergy || 100,
      },
      tooltip: {
        theme: 'dark',
        fixed: {
          enabled: false,
        },
        x: {
          show: true,
        },
        y: {
          title: {
            formatter: (seriesName: string) => seriesName,
          },
          formatter: (val: number) => formatkWhEnergy(val),
        },
        marker: {
          show: false,
        },
      },
    },
    series: [
      {
        name: t('energy'),
        data: sessions.map((session) => {
          return {
            x: session.timestamp,
            y: session.energy,
          };
        }),
      },
    ],
  };

  return (
    <CardWidget
      chartData={chartData}
      color="dark"
      isLoading={isLoading}
      value={formatkWhEnergy(totalEnergyConsumption)}
      name={t('energy')}
      icon={<IconBolt />}
    />
  );
};
